import { dehydrate } from 'react-query';
import BlockBuilder from '@/components/blocks/BlockBuilder';
import Layout from '@/components/global/Layout';
import Hero from '@/components/shared/Hero';
import { determinei18nLocale } from '@/helpers/determineLocale';
import { I18nLocales, ILocales } from '@/interfaces/locales.interfaces';
import client from '@/lib/apolloClient';
import { fetchGlobalData, getSingularPage } from '@/lib/strapiQueries';
import { GetStaticProps, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import dynamic from 'next/dynamic';
import { queryClient } from './_app';

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
    const { locale, draftMode = false } = context;
    const cleanedLocale = locale !== ILocales.default ? determinei18nLocale(locale as ILocales) : I18nLocales.nz;

    const ifErrorProps = { props: {} };

    await queryClient.prefetchQuery(`global-${locale}`, () => fetchGlobalData(cleanedLocale), { staleTime: Infinity });

    try {
        const page = await getSingularPage(client, cleanedLocale, '/', draftMode); // homepage always "/"

        if (!page) {
            return ifErrorProps;
        }

        return {
            props: {
                page: page[0],
                locale: locale,
                dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
                preview: draftMode,
            },
        };
    } catch (error) {
        return ifErrorProps;
    }
};

const Home = ({ page, locale, preview }: InferGetStaticPropsType<typeof getStaticProps>) => {
    if (!page || !global || !locale || locale === ILocales.default) {
        const Error = dynamic(() => import('@/components/global/Error'));
        return <Error statusCode={404} />;
    }
    const { hero, blocks } = page.attributes;

    return (
        <Layout page={page} preview={preview}>
            {hero && <Hero {...hero} />}
            {blocks && <BlockBuilder blocks={blocks} />}
        </Layout>
    );
};

export default Home;
